import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Grid,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  DeviceHub as DeviceIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  Download as DownloadIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material";
import Loader from "../../components/loader/Loader";
import { downloadFileFromS3 } from "../../utils/commonFunctions";

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;
const bucketName = process.env.REACT_APP_BUSINESSDEVICES_BUCKET_NAME;

interface DeviceFile {
  fileName: string;
  key: string;
}

interface DateTimeFolder {
  datetime: string;
  files: DeviceFile[];
}

interface DeviceData {
  deviceId: string;
  datetimeFolders: DateTimeFolder[];
}

const BusinessDB = () => {
  const { businessId }: any = useParams();
  const [dbData, setDbData] = useState<DeviceData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const fetchBusinessDB = async () => {
    if (!businessId) {
      setError("Business ID is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const accessToken = localStorage.getItem("accessToken") || "";
      const response = await axios({
        method: "get",
        url: `${apiEndpoint}/businessdevicemanagementnode/v1/businessdevice/${businessId}/dbfiles`,
        headers: {
          Authorization: `bearer ${accessToken}`,
          "Content-Type": "application/json"
        }
      });

      console.log("Business DB API Response:", response.data);
      setDbData(response.data);
    } catch (err: any) {
      console.error("Error fetching Business DB data:", err);
      setError(err.response?.data?.message || "Failed to fetch Device DB data");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBusinessDB();
  }, [businessId]);

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const getFileTypeIcon = (fileName: string) => {
    if (fileName.includes(".log")) {
      return <FileIcon sx={{ color: "#1976d2" }} />;
    } else if (fileName.includes(".sqlite")) {
      return <FileIcon sx={{ color: "#388e3c" }} />;
    }
    return <FileIcon sx={{ color: "#757575" }} />;
  };

  const getFileTypeChip = (fileName: string) => {
    if (fileName.includes("applogs")) {
      return (
        <Chip
          label="App Logs"
          size="small"
          color="primary"
          variant="outlined"
        />
      );
    } else if (fileName.includes("crashlogs")) {
      return (
        <Chip
          label="Crash Logs"
          size="small"
          color="error"
          variant="outlined"
        />
      );
    } else if (fileName.includes("database")) {
      return (
        <Chip
          label="Database"
          size="small"
          color="success"
          variant="outlined"
        />
      );
    }
    return (
      <Chip label="File" size="small" color="default" variant="outlined" />
    );
  };

  const handleDownload = (file: DeviceFile) => {
    try {
      if (!bucketName) {
        console.error("Bucket name not configured");
        return;
      }

      // Construct the S3 URL using the bucket name and file key
      const s3Url = `${bucketName}/${file.key}`;

      console.log("Downloading file:", file.fileName, "from URL:", s3Url);

      // Use the existing download function
      downloadFileFromS3(s3Url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchBusinessDB();
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3
        }}
      >
        <Typography variant="h5" sx={{ color: "#223354", fontWeight: 600 }}>
          Device Database Files
        </Typography>
        <IconButton
          onClick={handleRefresh}
          disabled={isRefreshing}
          color="primary"
          aria-label="refresh"
          sx={{ color: "#223354" }}
        >
          {isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
        </IconButton>
      </Box>

      {dbData.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center", backgroundColor: "#f8f9fa" }}>
          <Typography variant="h6" color="text.secondary">
            No device database files found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            There are currently no database files available for this business.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {dbData.map((device, deviceIndex) => (
            <Grid item xs={12} key={device.deviceId}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  borderRadius: 2
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Accordion defaultExpanded={deviceIndex === 0}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: "#f0f2f5",
                        "&:hover": {
                          backgroundColor: "#e8eaed"
                        }
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%"
                        }}
                      >
                        <DeviceIcon sx={{ color: "#223354", mr: 2 }} />
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: "#223354" }}
                        >
                          Device {device.deviceId}
                        </Typography>
                        <Chip
                          label={`${device.datetimeFolders.length} backup${
                            device.datetimeFolders.length !== 1 ? "s" : ""
                          }`}
                          size="small"
                          sx={{
                            ml: "auto",
                            backgroundColor: "#223354",
                            color: "#ffffff"
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Box>
                        {device.datetimeFolders.map((folder, folderIndex) => (
                          <Accordion
                            key={folder.datetime}
                            sx={{
                              "&:before": { display: "none" },
                              boxShadow: "none",
                              borderBottom:
                                folderIndex < device.datetimeFolders.length - 1
                                  ? "1px solid #e0e0e0"
                                  : "none"
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                backgroundColor: "#fafafa",
                                "&:hover": {
                                  backgroundColor: "#f5f5f5"
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%"
                                }}
                              >
                                <ScheduleIcon
                                  sx={{ color: "#666", mr: 2, fontSize: 20 }}
                                />
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {formatDateTime(folder.datetime)}
                                </Typography>
                                <Chip
                                  label={`${folder.files.length} file${
                                    folder.files.length !== 1 ? "s" : ""
                                  }`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: "auto" }}
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <List dense sx={{ backgroundColor: "#ffffff" }}>
                                {folder.files.map((file, fileIndex) => (
                                  <ListItem
                                    key={file.key}
                                    sx={{
                                      borderBottom:
                                        fileIndex < folder.files.length - 1
                                          ? "1px solid #f0f0f0"
                                          : "none",
                                      "&:hover": {
                                        backgroundColor: "#f8f9fa"
                                      }
                                    }}
                                  >
                                    <ListItemIcon>
                                      {getFileTypeIcon(file.fileName)}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500 }}
                                          >
                                            {file.fileName}
                                          </Typography>
                                          {getFileTypeChip(file.fileName)}
                                        </Box>
                                      }
                                      secondary={
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Key: {file.key}
                                        </Typography>
                                      }
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDownload(file)}
                                      sx={{ color: "#223354" }}
                                    >
                                      <DownloadIcon />
                                    </IconButton>
                                  </ListItem>
                                ))}
                              </List>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default BusinessDB;
